/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';

import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const mod = 'coupons';

const initAttr = {
  name: '',
  discount_type: 'fixed',
  discount_value: '',
  max_discount_amount: '',
  min_order_amount: '',
  is_condition: 'N',
  is_min_amount: 'N',
  start_date: Func.dateNow(),
  end_date: Func.dateAdd(30),
  enable: 'Y',
};

function CouponCreate({ open, onBack }) {
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();

  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const handleClose = () => {
    setAttr(initAttr);
    setConfirm(false);
    onBack();
  };

  // ---- Save ----
  const saveConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) {
      showNotify('กรุณากรอกชื่อคูปอง', 'error');
      return;
    }
    if (!Func.verifyLength(attr.discount_value.toString(), 1)) {
      showNotify('กรุณากรอกมูลค่าส่วนลด', 'error');
      return;
    }
    if (attr.is_min_amount === 'Y' && !Func.verifyLength(attr.min_order_amount.toString(), 1)) {
      showNotify('กรุณากรอกยอดสั่งซื้อขั้นต่ำ', 'error');
      return;
    }
    setConfirm(true);
  };

  const saveCoupon = () => {
    setLoading(true);
    setConfirm(false);

    FetchData('POST', `/${mod}`, app.token, {
      name: attr.name,
      discount_type: attr.discount_type,
      discount_value: parseFloat(attr.discount_value) || 0,
      max_discount_amount:
        attr.max_discount_amount !== '' ? parseFloat(attr.max_discount_amount) : null,
      min_order_amount: attr.is_min_amount === 'Y' ? parseFloat(attr.min_order_amount) || 0 : null,
      is_condition: attr.is_condition,
      is_min_amount: attr.is_min_amount,
      start_date: attr.start_date,
      end_date: attr.end_date,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        showNotify(t('alert.addcomplete'), 'success');
        setAttr(initAttr);
        onBack(result.id ?? null);
      } else {
        showNotify(t('alert.saveerror'), 'error');
      }
    });
  };

  // ---- Render: Form fields ----
  const renderForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MDInput
          label='ชื่อคูปอง *'
          fullWidth
          variant='standard'
          value={attr.name}
          onChange={(e) => setAttr({ ...attr, name: e.target.value })}
          inputProps={{ autoComplete: 'off' }}
        />
      </Grid>
      <Grid item xs={12}>
        <MDInput
          fullWidth
          label='ประเภทส่วนลด *'
          value={attr.discount_type}
          select
          variant='standard'
          onChange={(e) => setAttr({ ...attr, discount_type: e.target.value })}
        >
          <MenuItem value='fixed'>คงที่ (฿)</MenuItem>
          <MenuItem value='percentage'>เปอร์เซ็นต์ (%)</MenuItem>
        </MDInput>
      </Grid>
      <Grid item xs={12}>
        <MDInput
          label={`มูลค่าส่วนลด * ${attr.discount_type === 'percentage' ? '(%)' : '(฿)'}`}
          fullWidth
          variant='standard'
          value={attr.discount_value}
          onChange={(e) => setAttr({ ...attr, discount_value: e.target.value })}
          inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
        />
      </Grid>
      {attr.discount_type === 'percentage' && (
        <Grid item xs={12}>
          <MDInput
            label='ส่วนลดสูงสุด (฿)'
            fullWidth
            variant='standard'
            value={attr.max_discount_amount}
            onChange={(e) => setAttr({ ...attr, max_discount_amount: e.target.value })}
            inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <MDCheckbox
          label='มีเงื่อนไขยอดขั้นต่ำ'
          checked={attr.is_min_amount === 'Y'}
          onChange={(e) => setAttr({ ...attr, is_min_amount: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
      {attr.is_min_amount === 'Y' && (
        <Grid item xs={12}>
          <MDInput
            label='ยอดสั่งซื้อขั้นต่ำ (฿) *'
            fullWidth
            variant='standard'
            value={attr.min_order_amount}
            onChange={(e) => setAttr({ ...attr, min_order_amount: e.target.value })}
            inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <MDCheckbox
          label='ใช้ได้เฉพาะสินค้าที่กำหนด'
          checked={attr.is_condition === 'Y'}
          onChange={(e) => setAttr({ ...attr, is_condition: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
      <Grid item xs={6}>
        <MDDatePicker
          onChange={([date]) => setAttr({ ...attr, start_date: Func.dateFormat(date) })}
          value={new Date(attr.start_date)}
          input={{ label: 'วันที่เริ่มต้น *', variant: 'standard', fullWidth: true }}
        />
      </Grid>
      <Grid item xs={6}>
        <MDDatePicker
          onChange={([date]) => setAttr({ ...attr, end_date: Func.dateFormat(date) })}
          value={new Date(attr.end_date)}
          input={{ label: 'วันที่สิ้นสุด *', variant: 'standard', fullWidth: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <MDCheckbox
          label={t('attr.enable')}
          checked={attr.enable === 'Y'}
          onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Dialog
        open={open}
        maxWidth='sm'
        fullWidth
        keepMounted
        aria-describedby='dialog-create-coupon'
        onClose={handleClose}
      >
        <DialogTitle>
          <MDTypography variant='h6'>สร้างคูปองใหม่</MDTypography>
          <IconButton
            aria-label='close'
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={1}>{renderForm()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={saveCoupon}>
                  <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={saveConfirm}>
              <Icon>save</Icon>&nbsp;{t('element.save')}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>

      {notify}
    </>
  );
}

CouponCreate.defaultProps = {
  open: false,
};

CouponCreate.propTypes = {
  open: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
};

export default CouponCreate;
